import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { User } from '@shared/interfaces/user.interface';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment.dev';
import { UserInfoDto } from '@shared/interfaces/dtos/user-info-dto.interface';
import { HomesService } from '@shared/services/homes.service';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly oAuth = inject(OAuthService);
  private readonly http = inject(HttpClient);
  private readonly homesService = inject(HomesService);

  public isConnected$ = new BehaviorSubject<boolean>(false);
  public user$ = new BehaviorSubject<User | null>(null);
  public userInfos = signal<UserInfoDto>({} as UserInfoDto);

  public readonly isISignEnabled = computed<boolean>(
    () => this.userInfos().home?.iSignEnabled ?? false,
  );
  public readonly canUseISign = computed<boolean>(
    () =>
      this.isISignEnabled() &&
      (this.userInfos().profile?.hasAccessToIsign ?? false),
  );

  private inactivityTimeOut: ReturnType<typeof setTimeout> | undefined =
    undefined;

  public get sso(): null | string {
    return sessionStorage.getItem('id_token_claims_obj');
  }

  public get hasValidAccessToken(): boolean {
    return this.oAuth.hasValidAccessToken();
  }

  constructor() {
    ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'].forEach(
      (event) => {
        window.addEventListener(event, this.resetTimer);
      },
    );
  }

  public login(): void {
    this.oAuth.initLoginFlow();
  }

  public logout(): void {
    this.oAuth.logOut();
    this.http.get(`${environment.baseUrl}/logout`).subscribe(() => {
      this.isConnected$.next(false);
      this.user$.next(null);
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = '/';
    });
  }

  public connectUser(): Observable<unknown> {
    return this.http.get(
      `${environment.baseUrl}/${this.homesService.currentVisitedHome$.value?.name}/users/connected`,
    );
  }

  public fetchUserInfos(): Observable<UserInfoDto> {
    return this.http
      .get<UserInfoDto>(`${environment.baseUrl}/users/info`)
      .pipe(tap((infos) => this.userInfos.set(infos)));
  }

  async loadUserProfile(): Promise<void> {
    const profile = await this.oAuth.loadUserProfile();
    const { info } = profile as {
      info: {
        email: string;
        email_verified: boolean;
        family_name: string;
        name: string;
        given_name: string;
        preferred_username: string;
      };
    };
    const user: User = {} as User;
    user.email = info.email;
    user.isEmailVerified = info.email_verified;
    user.name = info.name;
    user.lastName = info.family_name;
    user.givenName = info.given_name;
    user.prefUserName = info.preferred_username;
    this.user$.next(user);
  }

  private resetTimer = (): void => {
    clearTimeout(this.inactivityTimeOut);
    this.inactivityTimeOut = setTimeout(
      () => {
        this.logout();
      },
      60 * 60 * 1000,
    );
  };
}
